// All filtering, grouping, and metric derivation for the Inbound Calls demo.
// Every KPI, chart bar, and breakdown is computed from the same call rows the
// table renders, so the numbers always reconcile — nothing is hardcoded.
//
// Day bucketing uses Intl.DateTimeFormat with the demo line's explicit IANA
// timezone (not utils/parseTime, which uses the viewer's browser timezone and
// could shift calls across days).

import { knowledgeEntryById } from "./mockData";
import {
  CallOutcome,
  CoverageHours,
  CoverageSchedule,
  DayCoverage,
  InboundCall,
  KnowledgeTier,
  ReasonCategory,
  WeekdayKey,
} from "./types";

export const REASON_LABELS: Record<ReasonCategory, string> = {
  SCHEDULING: "Scheduling",
  INSURANCE_ACCEPTANCE: "Insurance acceptance",
  THERAPY_LOGISTICS: "Therapy logistics",
  BILLING_INVOICING: "Billing / invoicing",
  RECORDS_FORMS: "Records / forms",
  PROVIDER_QUESTION: "Provider question",
  OTHER: "Other",
};

export const OUTCOME_LABELS: Record<CallOutcome, string> = {
  ANSWERED_FROM_KNOWLEDGE: "Answered from knowledge bank",
  APPOINTMENT_REQUESTED: "Cancel appointment requested",
  FOLLOW_UP_NEEDED: "Follow-up needed",
  UNRESOLVED: "Unresolved",
  APPOINTMENT_SCHEDULED: "Appointment scheduled",
};

export interface KpiSummary {
  totalCalls: number;
  answeredFromKnowledge: number;
  appointmentsScheduled: number;
  appointmentRequests: number;
  followUpsNeeded: number;
  unresolved: number;
}

export interface DailyVolumeBucket {
  /** Stable YYYY-MM-DD key in the demo timezone. */
  dayKey: string;
  /** Short display label, e.g. "Mon 8/17". */
  label: string;
  isWeekend: boolean;
  count: number;
}

export interface BreakdownRow {
  key: string;
  label: string;
  count: number;
}

export type DayType = "WEEKDAY" | "WEEKEND";

export type TimeOfDay = "MORNING" | "AFTERNOON" | "EVENING";

/** "NONE" means the assistant had no knowledge bank answer to draw on. */
export type KnowledgeSource = KnowledgeTier | "NONE";

export type SortMode = "NEWEST" | "OLDEST" | "CALLER";

export const DAY_TYPE_LABELS: Record<DayType, string> = {
  WEEKDAY: "Weekdays",
  WEEKEND: "Weekends",
};

export const TIME_OF_DAY_LABELS: Record<TimeOfDay, string> = {
  MORNING: "Morning (before 12pm)",
  AFTERNOON: "Afternoon (12–5pm)",
  EVENING: "Evening (5pm+)",
};

export const KNOWLEDGE_SOURCE_LABELS: Record<KnowledgeSource, string> = {
  ORG: "Organization knowledge bank",
  PRACTICE: "Practice overlay",
  NONE: "No knowledge bank answer used",
};

export const SORT_MODE_LABELS: Record<SortMode, string> = {
  NEWEST: "Newest first",
  OLDEST: "Oldest first",
  CALLER: "Caller A–Z",
};

/** Every field is optional and all supplied fields are ANDed together. */
export interface CallFilters {
  /** Free text matched across caller, recap, next step, and transcript. */
  query?: string;
  /** Inclusive YYYY-MM-DD bounds in the line's timezone. */
  dateFrom?: string;
  dateTo?: string;
  dayType?: DayType;
  timeOfDay?: TimeOfDay;
  practiceId?: string;
  /** Normalized (digits-only) caller phone. */
  callerPhone?: string;
  repeatCallersOnly?: boolean;
  reason?: ReasonCategory;
  outcome?: CallOutcome;
  knowledgeSource?: KnowledgeSource;
  needsFollowUpOnly?: boolean;
}

export interface CallerGroup {
  normalizedPhone: string;
  callerName: string;
  displayPhone: string;
  calls: InboundCall[];
}

export interface DaySection {
  /** Stable YYYY-MM-DD key in the demo timezone. */
  dayKey: string;
  /** Long display label, e.g. "Monday, August 17th". */
  label: string;
  calls: InboundCall[];
}

export function getDayKey(isoTimestamp: string, timeZone: string): string {
  // en-CA yields YYYY-MM-DD, which sorts lexicographically.
  return new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date(isoTimestamp));
}

export function isWeekendDay(isoTimestamp: string, timeZone: string): boolean {
  const weekday = new Intl.DateTimeFormat("en-US", {
    timeZone,
    weekday: "short",
  }).format(new Date(isoTimestamp));
  return weekday === "Sat" || weekday === "Sun";
}

export function formatDayLabel(isoTimestamp: string, timeZone: string): string {
  const date = new Date(isoTimestamp);
  const weekday = new Intl.DateTimeFormat("en-US", {
    timeZone,
    weekday: "short",
  }).format(date);
  const monthDay = new Intl.DateTimeFormat("en-US", {
    timeZone,
    month: "numeric",
    day: "numeric",
  }).format(date);
  return `${weekday} ${monthDay}`;
}

const ORDINAL_RULES = new Intl.PluralRules("en-US", { type: "ordinal" });

const ORDINAL_SUFFIXES: Record<string, string> = {
  one: "st",
  two: "nd",
  few: "rd",
  other: "th",
};

/** Long-form day header for the separators between days, e.g. "Monday, August 17th". */
export function formatDaySeparatorLabel(
  isoTimestamp: string,
  timeZone: string
): string {
  const date = new Date(isoTimestamp);
  // The day number is the last token of this format, so the ordinal suffix
  // can simply be appended.
  const base = new Intl.DateTimeFormat("en-US", {
    timeZone,
    weekday: "long",
    month: "long",
    day: "numeric",
  }).format(date);
  const dayOfMonth = Number(
    new Intl.DateTimeFormat("en-US", { timeZone, day: "numeric" }).format(date)
  );
  return `${base}${ORDINAL_SUFFIXES[ORDINAL_RULES.select(dayOfMonth)] ?? "th"}`;
}

export function formatCallTime(isoTimestamp: string, timeZone: string): string {
  return new Intl.DateTimeFormat("en-US", {
    timeZone,
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(isoTimestamp));
}

/** "17:00" from a time input becomes "5:00 PM" for display. */
export function formatClockTime(value: string): string {
  const [hours, minutes] = value.split(":").map(Number);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) {
    return value;
  }
  const hour12 = hours % 12 === 0 ? 12 : hours % 12;
  return `${hour12}:${String(minutes).padStart(2, "0")} ${
    hours < 12 ? "AM" : "PM"
  }`;
}

/** Monday-first, the order the editor and every label walk the week in. */
export const WEEKDAY_KEYS: WeekdayKey[] = [
  "MON",
  "TUE",
  "WED",
  "THU",
  "FRI",
  "SAT",
  "SUN",
];

export const WEEKDAY_LABELS: Record<WeekdayKey, string> = {
  MON: "Mon",
  TUE: "Tue",
  WED: "Wed",
  THU: "Thu",
  FRI: "Fri",
  SAT: "Sat",
  SUN: "Sun",
};

export const WEEKDAY_FULL_LABELS: Record<WeekdayKey, string> = {
  MON: "Monday",
  TUE: "Tuesday",
  WED: "Wednesday",
  THU: "Thursday",
  FRI: "Friday",
  SAT: "Saturday",
  SUN: "Sunday",
};

export const MINUTES_IN_DAY = 1440;

/**
 * Minutes from the start of the day, or null if the input is blank or
 * malformed. `isEnd` resolves the one ambiguity in the format: "00:00" closing
 * a range is the end of the day (1440), not its start.
 */
export function coverageMinutes(value: string, isEnd = false): number | null {
  const match = /^(\d{2}):(\d{2})$/.exec(value);
  if (!match) {
    return null;
  }
  const minutes = Number(match[1]) * 60 + Number(match[2]);
  if (minutes > MINUTES_IN_DAY) {
    return null;
  }
  return isEnd && minutes === 0 ? MINUTES_IN_DAY : minutes;
}

/** "5:00 PM – 10:00 PM", or "5:00 PM – midnight" for a range that closes the day. */
export function formatCoverageHours(hours: CoverageHours): string {
  const end = hours.end === "00:00" ? "midnight" : formatClockTime(hours.end);
  return `${formatClockTime(hours.start)} – ${end}`;
}

/** What one day reads as on its own. Empty string when the practice takes every call. */
export function dayCoverageLabel(coverage: DayCoverage): string {
  if (coverage.allDay) {
    return "all day";
  }
  return coverage.windows.map(formatCoverageHours).join(" and ");
}

export function isDayCovered(coverage: DayCoverage): boolean {
  return coverage.allDay || coverage.windows.length > 0;
}

/**
 * Identity of a day's coverage, for collapsing consecutive days that say the
 * same thing into one run. Windows are ignored while `allDay` is set, since
 * they are dormant and two all-day days are the same day to a reader.
 */
function dayCoverageKey(coverage: DayCoverage): string {
  if (coverage.allDay) {
    return "ALL_DAY";
  }
  return coverage.windows.map((w) => `${w.start}-${w.end}`).join("|");
}

export interface CoverageRun {
  days: WeekdayKey[];
  coverage: DayCoverage;
}

/** Consecutive days with identical coverage, so "Mon–Fri" is one row not five. */
export function groupCoverageRuns(schedule: CoverageSchedule): CoverageRun[] {
  const runs: CoverageRun[] = [];
  let previousKey: string | null = null;
  for (const day of WEEKDAY_KEYS) {
    const coverage = schedule[day];
    const key = dayCoverageKey(coverage);
    const current = runs[runs.length - 1];
    if (current && key === previousKey) {
      current.days.push(day);
    } else {
      runs.push({ days: [day], coverage });
    }
    previousKey = key;
  }
  return runs;
}

/** "Mon" for one day, "Mon–Fri" for a run of them. */
export function formatDayRun(days: WeekdayKey[]): string {
  if (days.length === 1) {
    return WEEKDAY_LABELS[days[0]];
  }
  return `${WEEKDAY_LABELS[days[0]]}–${WEEKDAY_LABELS[days[days.length - 1]]}`;
}

/** The one-line answer to "what are your business hours?", minus the timezone. */
export function coverageScheduleLabel(schedule: CoverageSchedule): string {
  if (WEEKDAY_KEYS.every((day) => schedule[day].allDay)) {
    return "No business hours set — the AI assistant handles every call, around the clock";
  }
  const covered = groupCoverageRuns(schedule).filter((run) =>
    isDayCovered(run.coverage)
  );
  if (covered.length === 0) {
    return "No business hours set — every call rings the practice";
  }
  const runs = covered.map(
    (run) => `${formatDayRun(run.days)} ${dayCoverageLabel(run.coverage)}`
  );
  return `Business hours: ${runs.join(", ")}. The AI assistant handles calls outside these hours`;
}

/**
 * The first thing wrong with the schedule, phrased for the editor, or null when
 * it is safe to apply. Overlaps are rejected rather than silently merged: two
 * rules covering the same minute is exactly the ambiguity that makes a routing
 * bug hard to explain later.
 */
export function validateCoverageSchedule(
  schedule: CoverageSchedule
): string | null {
  for (const day of WEEKDAY_KEYS) {
    const coverage = schedule[day];
    if (coverage.allDay) {
      continue;
    }
    const spans: Array<{ start: number; end: number }> = [];
    for (const window of coverage.windows) {
      const start = coverageMinutes(window.start);
      const end = coverageMinutes(window.end, true);
      if (start === null || end === null) {
        return `Fill in both sides of every time range on ${WEEKDAY_FULL_LABELS[day]}.`;
      }
      if (end <= start) {
        return `A time range on ${WEEKDAY_FULL_LABELS[day]} has to end after it starts. Use midnight (12:00 AM) as the end to run to the close of the day.`;
      }
      spans.push({ start, end });
    }
    const sorted = [...spans].sort((a, b) => a.start - b.start);
    for (let i = 1; i < sorted.length; i += 1) {
      if (sorted[i].start < sorted[i - 1].end) {
        return `Two time ranges on ${WEEKDAY_FULL_LABELS[day]} overlap.`;
      }
    }
  }
  return null;
}

/**
 * Sorts each day's windows by start time. Applied on write so the stored
 * schedule reads in the order the day happens no matter what order the
 * practice typed the rows in.
 */
export function normalizeCoverageSchedule(
  schedule: CoverageSchedule
): CoverageSchedule {
  const normalized = {} as CoverageSchedule;
  for (const day of WEEKDAY_KEYS) {
    const coverage = schedule[day];
    normalized[day] = {
      allDay: coverage.allDay,
      windows: [...coverage.windows].sort(
        (a, b) =>
          (coverageMinutes(a.start) ?? 0) - (coverageMinutes(b.start) ?? 0)
      ),
    };
  }
  return normalized;
}

export function computeKpis(calls: InboundCall[]): KpiSummary {
  return {
    totalCalls: calls.length,
    answeredFromKnowledge: calls.filter(
      (c) => c.outcome === "ANSWERED_FROM_KNOWLEDGE"
    ).length,
    appointmentsScheduled: calls.filter(
      (c) => c.outcome === "APPOINTMENT_SCHEDULED"
    ).length,
    appointmentRequests: calls.filter(
      (c) => c.outcome === "APPOINTMENT_REQUESTED"
    ).length,
    followUpsNeeded: calls.filter(
      (c) => c.followUpTask !== undefined && c.followUpTask.status === "PENDING"
    ).length,
    unresolved: calls.filter((c) => c.outcome === "UNRESOLVED").length,
  };
}

export function buildDailyVolume(
  calls: InboundCall[],
  timeZone: string
): DailyVolumeBucket[] {
  const buckets = new Map<string, DailyVolumeBucket>();
  for (const call of calls) {
    const dayKey = getDayKey(call.receivedAt, timeZone);
    const existing = buckets.get(dayKey);
    if (existing) {
      existing.count += 1;
    } else {
      buckets.set(dayKey, {
        dayKey,
        label: formatDayLabel(call.receivedAt, timeZone),
        isWeekend: isWeekendDay(call.receivedAt, timeZone),
        count: 1,
      });
    }
  }
  return Array.from(buckets.values()).sort((a, b) =>
    a.dayKey.localeCompare(b.dayKey)
  );
}

function buildBreakdown(
  calls: InboundCall[],
  getKey: (call: InboundCall) => string,
  labels: Record<string, string>
): BreakdownRow[] {
  const counts = new Map<string, number>();
  for (const call of calls) {
    const key = getKey(call);
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return Array.from(counts.entries())
    .map(([key, count]) => ({ key, label: labels[key] ?? key, count }))
    .sort((a, b) => b.count - a.count);
}

export function breakdownByReason(calls: InboundCall[]): BreakdownRow[] {
  return buildBreakdown(calls, (c) => c.reason, REASON_LABELS);
}

export function breakdownByOutcome(calls: InboundCall[]): BreakdownRow[] {
  return buildBreakdown(calls, (c) => c.outcome, OUTCOME_LABELS);
}

/** Hour 0–23 in the line's timezone. */
export function getHour(isoTimestamp: string, timeZone: string): number {
  return Number(
    new Intl.DateTimeFormat("en-US", {
      timeZone,
      hour: "2-digit",
      hourCycle: "h23",
    }).format(new Date(isoTimestamp))
  );
}

export function getTimeOfDay(
  isoTimestamp: string,
  timeZone: string
): TimeOfDay {
  const hour = getHour(isoTimestamp, timeZone);
  if (hour < 12) {
    return "MORNING";
  }
  return hour < 17 ? "AFTERNOON" : "EVENING";
}

/** Knowledge tiers the assistant drew on during the call, if any. */
export function knowledgeTiersUsed(call: InboundCall): Set<KnowledgeTier> {
  return new Set(
    citedKnowledgeEntryIds(call).map((id) => knowledgeEntryById(id).tier)
  );
}

/**
 * Everything a free-text search should reach: who called, what the practice
 * needs to act on, and what was actually said — including the knowledge bank answers
 * behind each cited reply.
 */
function searchCorpus(call: InboundCall): string {
  const parts = [
    call.callerName,
    call.callerPhone,
    normalizePhone(call.callerPhone),
    call.patientName ?? "",
    call.callerRelationship ?? "",
    call.recapText,
    call.nextStep,
    REASON_LABELS[call.reason],
    OUTCOME_LABELS[call.outcome],
    call.followUpTask?.description ?? "",
    ...(call.followUpTask?.patientInfoCollected ?? []),
    ...call.transcript.map((turn) => turn.text),
  ];
  for (const id of citedKnowledgeEntryIds(call)) {
    const entry = knowledgeEntryById(id);
    parts.push(entry.id, entry.question, entry.answer);
  }
  return parts.join("\n").toLowerCase();
}

/** All whitespace-separated terms must appear somewhere in the call. */
export function matchesQuery(call: InboundCall, query: string): boolean {
  const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (terms.length === 0) {
    return true;
  }
  const corpus = searchCorpus(call);
  return terms.every((term) => corpus.includes(term));
}

export function applyFilters(
  calls: InboundCall[],
  filters: CallFilters,
  timeZone: string
): InboundCall[] {
  // Repeat callers are judged against the whole input set, so the flag keeps
  // meaning "called more than once" rather than "matched twice".
  const callCounts = new Map<string, number>();
  for (const call of calls) {
    const phone = normalizePhone(call.callerPhone);
    callCounts.set(phone, (callCounts.get(phone) ?? 0) + 1);
  }

  return calls.filter((call) => {
    const dayKey = getDayKey(call.receivedAt, timeZone);
    if (filters.dateFrom !== undefined && dayKey < filters.dateFrom) {
      return false;
    }
    if (filters.dateTo !== undefined && dayKey > filters.dateTo) {
      return false;
    }
    if (filters.dayType !== undefined) {
      const isWeekend = isWeekendDay(call.receivedAt, timeZone);
      if (isWeekend !== (filters.dayType === "WEEKEND")) {
        return false;
      }
    }
    if (
      filters.timeOfDay !== undefined &&
      getTimeOfDay(call.receivedAt, timeZone) !== filters.timeOfDay
    ) {
      return false;
    }
    if (
      filters.practiceId !== undefined &&
      call.practiceId !== filters.practiceId
    ) {
      return false;
    }
    if (
      filters.callerPhone !== undefined &&
      normalizePhone(call.callerPhone) !== filters.callerPhone
    ) {
      return false;
    }
    if (
      filters.repeatCallersOnly === true &&
      (callCounts.get(normalizePhone(call.callerPhone)) ?? 0) < 2
    ) {
      return false;
    }
    if (filters.reason !== undefined && call.reason !== filters.reason) {
      return false;
    }
    if (filters.outcome !== undefined && call.outcome !== filters.outcome) {
      return false;
    }
    if (filters.knowledgeSource !== undefined) {
      const tiers = knowledgeTiersUsed(call);
      const matches =
        filters.knowledgeSource === "NONE"
          ? tiers.size === 0
          : tiers.has(filters.knowledgeSource);
      if (!matches) {
        return false;
      }
    }
    if (
      filters.needsFollowUpOnly === true &&
      (call.followUpTask === undefined || call.followUpTask.status !== "PENDING")
    ) {
      return false;
    }
    if (filters.query !== undefined && !matchesQuery(call, filters.query)) {
      return false;
    }
    return true;
  });
}

export function sortCalls(
  calls: InboundCall[],
  mode: SortMode
): InboundCall[] {
  const sorted = [...calls];
  switch (mode) {
    case "OLDEST":
      return sorted.sort(
        (a, b) =>
          new Date(a.receivedAt).getTime() - new Date(b.receivedAt).getTime()
      );
    case "CALLER":
      return sorted.sort(
        (a, b) =>
          a.callerName.localeCompare(b.callerName) ||
          new Date(b.receivedAt).getTime() - new Date(a.receivedAt).getTime()
      );
    default:
      return sortByReceivedDesc(sorted);
  }
}

export function sortByReceivedDesc(calls: InboundCall[]): InboundCall[] {
  return [...calls].sort(
    (a, b) =>
      new Date(b.receivedAt).getTime() - new Date(a.receivedAt).getTime()
  );
}

export function normalizePhone(phone: string): string {
  return phone.replace(/\D/g, "");
}

/** Masks all but the last two digits for list views, e.g. "(555) •••-••31". */
export function maskPhone(phone: string): string {
  const digits = normalizePhone(phone);
  if (digits.length < 4) {
    return "•••";
  }
  return `(${digits.slice(0, 3)}) •••-••${digits.slice(-2)}`;
}

/**
 * Groups calls by normalized caller phone so repeat callers appear as one
 * group. Heuristic only (shared family phones collide) — acceptable for the
 * demo, revisit for production.
 */
export function groupByCaller(calls: InboundCall[]): CallerGroup[] {
  const groups = new Map<string, CallerGroup>();
  for (const call of sortByReceivedDesc(calls)) {
    const normalizedPhone = normalizePhone(call.callerPhone);
    const existing = groups.get(normalizedPhone);
    if (existing) {
      existing.calls.push(call);
    } else {
      groups.set(normalizedPhone, {
        normalizedPhone,
        callerName: call.callerName,
        displayPhone: maskPhone(call.callerPhone),
        calls: [call],
      });
    }
  }
  return Array.from(groups.values());
}

/**
 * Entry ids cited by the call's agent turns, de-duplicated and in the order
 * they were used. Derived from the transcript so the summary can never
 * disagree with the line-level citations.
 */
export function citedKnowledgeEntryIds(call: InboundCall): string[] {
  const ids: string[] = [];
  for (const turn of call.transcript) {
    if (turn.knowledgeEntryId !== undefined && !ids.includes(turn.knowledgeEntryId)) {
      ids.push(turn.knowledgeEntryId);
    }
  }
  return ids;
}

/**
 * Splits an already-sorted list into consecutive runs that share a day, so the
 * caller's chosen order is preserved and never re-sorted. Only meaningful for
 * a chronologically sorted list; any other order would produce a run per call.
 */
export function groupByDay(
  calls: InboundCall[],
  timeZone: string
): DaySection[] {
  const sections: DaySection[] = [];
  for (const call of calls) {
    const dayKey = getDayKey(call.receivedAt, timeZone);
    const current = sections[sections.length - 1];
    if (current && current.dayKey === dayKey) {
      current.calls.push(call);
    } else {
      sections.push({
        dayKey,
        label: formatDaySeparatorLabel(call.receivedAt, timeZone),
        calls: [call],
      });
    }
  }
  return sections;
}

/** Unique callers in alphabetical order, for the caller filter dropdown. */
export function listCallerOptions(calls: InboundCall[]): CallerGroup[] {
  return groupByCaller(calls).sort((a, b) =>
    a.callerName.localeCompare(b.callerName)
  );
}

export function buildHandoffNote(
  call: InboundCall,
  practiceName: string,
  timeZone: string
): string {
  const lines = [
    `Inbound call — ${formatCallTime(
      call.receivedAt,
      timeZone
    )} (${practiceName})`,
    `Caller: ${call.callerName} ${call.callerPhone}${
      call.patientName ? ` — re: ${call.patientName}` : ""
    }`,
    `Reason: ${REASON_LABELS[call.reason]}`,
    `Recap: ${call.recapText}`,
    `Outcome: ${OUTCOME_LABELS[call.outcome]}`,
    `Next step: ${call.nextStep}`,
  ];
  if (call.followUpTask) {
    lines.push(
      `Callback: ${call.followUpTask.callbackNumber}, due by ${formatCallTime(
        call.followUpTask.dueBy,
        timeZone
      )}`
    );
  }
  return lines.join("\n");
}
